import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import LayoutContainer from 'components/Common/LayoutContainer'
import { rem } from 'polished'
import moment from 'moment'
import styled from 'styled-components'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import { countOccupants, formatOccupantsAsGuests, formatOccupantsShort } from 'lib/offer/occupancyUtils'
import TextButton from 'components/Luxkit/Button/TextButton'
import FilterChip from 'components/Luxkit/Chips/FilterChip'
import MultipleRoomsBookingOption from './MultipleRoomsBookingOption'
import useToggle from 'hooks/useToggle'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { OfferPageActions, OfferPageFilterTypes } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import Caption from 'components/Luxkit/Typography/Caption'
import { DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import MultipleRoomsBookingRoomsModal from './MultipleRoomsBookingRoomsModal'
import MultipleRoomsBookingContentFooter from './MultipleRoomsBookingContentFooter'
import { useInView } from 'react-intersection-observer'
import {
  FlightsInfoByRoom, getActivePackagesByRoom, getAvailablePackagesByRoom, getCalendarsToBeFetchedByRoom, getCheapestPackagesByRoom, getDurationPackageOptionsByRoom, getFlightsInfoByRoom, isLoadingByRoom, LoadingStatusByRoom,
  PackageOptionViewByRoom,
  PackagesOptionsViewsByRoom,
} from 'selectors/offerPage/multipleRoomBookingSelectors'

import { useAppDispatch } from 'hooks/reduxHooks'
import BnblModal from '../BnblModal/BnblModal'
import { initialiseBnblOfferModal } from 'actions/BnblModalActions'
import { countBy, EmptyArray, groupBy } from 'lib/array/arrayUtils'
import { getBnblPackageViews, OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import { getBnblPackageViewByNumberOfRooms } from '../BnblModal/utils'
import { arrayToStringListFormat } from 'lib/string/stringUtils'
import RequestConnectingRoomsInput from './RequestConnectingRoomsInput'
import StatusLabel from 'components/Luxkit/Label/StatusLabel'
import TextLink from 'components/Luxkit/TextLink'
import { showSnackbar } from 'components/Luxkit/Snackbar/AppSnackbar'
import PriceRowPriceCaption from 'components/Luxkit/PricePoints/PriceRowPriceCaption'
import PriceRowPrice from 'components/Luxkit/PricePoints/PriceRowPrice'
import LuxPlusPriceGuard from 'luxPlus/components/LuxPlusPriceGuard'
import PriceStackLuxPlusCompactPrice from 'components/Luxkit/PricePoints/LuxPlus/PriceStackLuxPlusCompactPrice'
import config from 'constants/config'
import Pane from 'components/Common/Pane'
import { getPackageViewPricingPerNight } from 'lib/offer/getPackageViewPricingPerNightUtils'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import ModalContext from 'contexts/ModalContext'
import AccountAccessModal, { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal'
import MultiRoomOfferBookButton from './MultiRoomOfferBookButton'
import { useIsMobileDevice } from 'hooks/useIsMobileDevice'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { mergeObjects } from 'lib/object/objectUtils'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { OptimizelyExperiments, OptimizelyFeatureFlags } from 'constants/optimizely'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { getOptimizelyExperimentVariation } from 'lib/optimizely/optimizelyUtils'
import { checkCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import MessageBanner from 'components/Luxkit/Banners/MessageBanner'
import { NO_24_7_SUPPORT_REGIONS } from 'constants/config/region'
import GeoContext from 'contexts/geoContext'
import ContactContext from 'contexts/contactContext'
import LinePhoneIcon from 'components/Luxkit/Icons/line/LinePhoneIcon'
import RequestAgentCallbackModal from 'supportAssistant/components/RequestAgentCallback/Modals/RequestAgentCallbackModal'
import { isCustomerSupportMode } from 'selectors/featuresSelectors'
import useHasGotToAnchor from 'hooks/useHasGotToAnchor'
import clsx from 'clsx'
import noop from 'lib/function/noop'
import { generateLuxLoyaltyPointsCalculatorHotelOptions, generateLuxLoyaltyPointsCalculatorFlightOptions } from 'luxLoyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import useLuxLoyaltyPointsCalculator from 'luxLoyalty/hooks/useLuxLoyaltyPointsCalculator'
import LuxLoyaltyPoints from 'luxLoyalty/components/LuxLoyaltyPoints'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import MultiRoomOfferAddToCartButton from './MultiRoomOfferAddToCartButton'
import MultiRoomOfferSelectRoomsButton from './MultiRoomOfferSelectRoomsButton'
import HotelFlightsToggleButtonV2 from '../FlashHotel/HotelFlightsToggleButtonV2'

const PackageOptionsContainer = styled(LayoutContainer)`
  padding: 0;
  position: relative;
`

const StyledHeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(20)};
  padding: 0 ${rem(16)};

  ${mediaQueryUp.desktop} {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-end;
    gap: 0;
    padding: 0;
  }
`

const FooterContainer = styled.div`
  ${mediaQueryUp.desktop} {
    padding: 0;
  }
`

const StyledWhiteBlockContainer = styled.div`
  background-color: #FFF;
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  padding: ${rem(16)};
`

const StyledPackageContainer = styled(StyledWhiteBlockContainer)`
  padding: 0;
`

const StyledRoomPackageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  padding: ${rem(16)};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.five};
`

const StyledRemoveButton = styled(TextButton)`
  padding: 0;
  margin: -${rem(4)} 0;
`

const StyledRoomTitle = styled(BodyText)`
  font-family: ${props => props.theme.font.secondary.family};
`

const StyledRoomOccupancyString = styled(BodyText)`
  margin-left: ${rem(8)};
`

const StyledActionFooterButtons = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${rem(24)};

  ${mediaQueryUp.desktop} {
    justify-content: flex-end;
    margin-left: auto;
  }
`

const NoPackagesContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${rem(16)};
  gap: ${rem(8)};
`

const NoPackagesTextBox = styled.div`
  display: flex;
  align-items: center;
  padding: ${rem(9)};
  background-color: ${props => props.theme.palette.neutral.default.seven};
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};

  ${mediaQueryUp.desktop} {
    flex-direction: row;
    justify-content: space-between;
    width: 100%;
  }

&.with-banner {
    flex-direction: column;
    margin-top: ${rem(8)};
    gap: ${rem(8)};

    ${mediaQueryUp.desktop} {
      flex-direction: row;
      margin-top: 0;
      align-self: flex-end;
      padding: 0;
    }
  }
`

const PointsContainer = styled.div`
  display: flex;
  justify-content: start;

  ${mediaQueryUp.tablet} {
    justify-content: end;
  }
`

interface FeedbackBannerMessage {
  error: {
    title: string;
    message?: string;
  } | null;
}

interface Props {
  rooms?: Array<App.RoomOccupants>;
  offer: App.Offer;
  duration?: number;
  activeFilter?: OfferPageFilterTypes;
  cheapestPackagesByRoom: PackageOptionViewByRoom;
  activePackagesByRoom: PackageOptionViewByRoom;
  isLoadingByRoom: LoadingStatusByRoom;
  calendarsToBeFetchedByRoom: LoadingStatusByRoom;
  flightsInfoByRoom: FlightsInfoByRoom;
  availablePackagesByRoom: PackagesOptionsViewsByRoom;
  durationPackagesByRoom: PackagesOptionsViewsByRoom;
  account?: App.AuthAccount;
  loggedIn?: boolean;
  baseBnblPackageViews: Array<OfferPackageOptionView>;
  roomTypesByRoom: { [roomId: string]: string };
  isMultiRoomConnected: boolean;
  isPerNightPricingTestEnabled: boolean;
  canRedeemLuxPlusBenefits: boolean;
  isCustomerSupportMode: boolean;
  userMultiItemCartEnabled: boolean;
  onStickyFooterToggle?: (visible: boolean) => void;
  checkIn?: string;
}

function MultipleRoomsBookingContent(props: Props) {
  const {
    rooms = EmptyArray,
    offer,
    duration,
    activeFilter,
    cheapestPackagesByRoom,
    activePackagesByRoom,
    isLoadingByRoom,
    calendarsToBeFetchedByRoom,
    flightsInfoByRoom,
    availablePackagesByRoom,
    durationPackagesByRoom,
    loggedIn: isLoggedIn,
    baseBnblPackageViews,
    roomTypesByRoom,
    isMultiRoomConnected,
    isPerNightPricingTestEnabled,
    canRedeemLuxPlusBenefits,
    isCustomerSupportMode,
    userMultiItemCartEnabled,
    onStickyFooterToggle = noop,
    checkIn,
  } = props

  const showModal = useContext(ModalContext)
  const offerPageDispatch = useOfferPageDispatch()
  const dispatch = useAppDispatch()

  const [reviewPageRef, reviewPageInView] = useInView()
  const [footerRef, footerInView] = useInView({ rootMargin: '0px 0px -100px 0px' })
  const { currentRegionCode } = useContext(GeoContext)
  const currentContact = useContext(ContactContext)

  const isFlightsForAllRoomsSelected = useMemo(() => Object.values(flightsInfoByRoom).every(flightsInfo => flightsInfo.isFlightsSelected), [flightsInfoByRoom])
  const isFlightsForAllRoomsAvailable = useMemo(() => Object.values(flightsInfoByRoom).every(flightsInfo => flightsInfo.isFlightsAvailable), [flightsInfoByRoom])

  const [buyNowBookLater, _, openBNBLModal, closeBNBLModal] = useToggle()
  const [isOpenRoomsModal, , openRoomsModal, closeRoomsModal] = useToggle()
  const [changedRoom, setChangedRoom] = useState<App.RoomOccupants | null>(null)

  const allRooms = useMemo(() => countOccupants(rooms), [rooms])
  const showGuestsLabel = allRooms.children || allRooms.infants
  const occupantsString = showGuestsLabel ? `${formatOccupantsAsGuests(allRooms)} • ${formatOccupantsShort(allRooms)}` : formatOccupantsShort(allRooms)
  const changedRoomIndex = useMemo(() => rooms.findIndex(room => room.roomId === changedRoom?.roomId), [rooms, changedRoom])
  const hasNoFlightPrices = useMemo(() => !!Object.values(flightsInfoByRoom).find(pkg => !pkg.flightPrice), [flightsInfoByRoom])

  const isMobileDevice = useIsMobileDevice()
  const isHotelAndFlightsToggleEnabled = isFlightsForAllRoomsAvailable && !offer.bundledWithFlightsOnly

  const onOpenRoomsModal = useCallback((room: App.RoomOccupants) => {
    setChangedRoom(room)

    openRoomsModal()
  }, [openRoomsModal])

  const onSelectPackage = useCallback((packageOption: App.Package) => {
    offerPageDispatch({
      type: OfferPageActions.selectRoomPackage,
      packageOption,
      room: changedRoom,
    })

    closeRoomsModal()
  }, [offerPageDispatch, changedRoom, closeRoomsModal])

  const onToggleFlightsForAllRooms = useCallback(() => {
    offerPageDispatch({
      type: OfferPageActions.toggleFlightsPrice,
      isFlightsSelected: !isFlightsForAllRoomsSelected,
    })
  }, [offerPageDispatch, isFlightsForAllRoomsSelected])

  const lastValidPackagesIdsByRoom = useRef<{ [roomId: string]: string }>({})
  const previousPackagesByRoom = useRef<PackageOptionViewByRoom>({})
  const previousDuration = useRef<number | null>(null)
  const selectionAmountByPackage = useRef<Map<string, number>>(new Map<string, number>())

  const packagesByRoom = useMemo(() => {
    const selectedPackages = { ...activePackagesByRoom }
    Object
      .entries(selectedPackages)
      // filters only rooms without package
      .filter(([, selectedPackage]) => !selectedPackage)
      .forEach(([roomId]) => {
        const isThisRoomLoading = isLoadingByRoom[roomId] || calendarsToBeFetchedByRoom[roomId]
        // If this room has a "loading" state, we select
        // the first available package for it, only for the
        // component to show the loading indicator
        selectedPackages[roomId] = isThisRoomLoading ? durationPackagesByRoom[roomId][0] : undefined
      })

    return selectedPackages
  }, [activePackagesByRoom, isLoadingByRoom, durationPackagesByRoom, calendarsToBeFetchedByRoom])

  const isCheapestPackagesActiveByRoom = useMemo(() => {
    const cheapestByRoom: Record<string, boolean> = {}
    Object
      .entries(packagesByRoom)
      .forEach(([roomId, packageOption]) => {
        cheapestByRoom[roomId] = !!(packageOption &&
          cheapestPackagesByRoom[roomId] &&
          cheapestPackagesByRoom[roomId].package.uniqueKey === packageOption.package.uniqueKey)
      })

    return cheapestByRoom
  }, [packagesByRoom, cheapestPackagesByRoom])

  const {
    totalRoomsPrice,
    totalRoomsBaseMemberPrice,
    totalRoomsMemberPrice,
    totalRoomsFlightPrice,
  } = useMemo(() => (
    Object.keys(packagesByRoom).reduce((acc, roomId) => {
      if (!packagesByRoom[roomId]) return acc

      const {
        hotelPrice,
        hotelMemberPrice,
        baseMemberPrice,
        flightPrice,
        propertyFees = 0,
      } = isPerNightPricingTestEnabled ? getPackageViewPricingPerNight(packagesByRoom[roomId]) : packagesByRoom[roomId]

      const { isFlightsSelected } = flightsInfoByRoom[roomId]

      const totalHotelPrice = hotelPrice + propertyFees
      const totalHotelMemberPrice = hotelMemberPrice + propertyFees
      const totalRoomPrice = isFlightsSelected ? totalHotelPrice + flightPrice : totalHotelPrice
      const totalRoomMemberPrice = isFlightsSelected ? totalHotelMemberPrice + flightPrice : totalHotelMemberPrice
      const totalFlightPrice = isFlightsSelected ? flightPrice : 0

      return {
        totalRoomsPrice: acc.totalRoomsPrice + totalRoomPrice,
        totalRoomsMemberPrice: acc.totalRoomsMemberPrice + totalRoomMemberPrice,
        totalRoomsBaseMemberPrice: acc.totalRoomsBaseMemberPrice + baseMemberPrice,
        totalRoomsFlightPrice: acc.totalRoomsFlightPrice + totalFlightPrice,
      }
    }, { totalRoomsPrice: 0, totalRoomsMemberPrice: 0, totalRoomsBaseMemberPrice: 0, totalRoomsFlightPrice: 0 })
  ), [packagesByRoom, flightsInfoByRoom, isPerNightPricingTestEnabled])

  const isBookNowBtnDisabled = useMemo(() => (
    Object.values(isLoadingByRoom).some(Boolean) ||
    Object.values(calendarsToBeFetchedByRoom).some(Boolean) ||
    Object.values(packagesByRoom).some(pkg => !pkg)
  ), [isLoadingByRoom, calendarsToBeFetchedByRoom, packagesByRoom])

  const onOpenBnblModal = useCallback(async(e: React.MouseEvent<HTMLAnchorElement>) => {
    const openAndInitaliseBnblModal = () => {
      e.preventDefault()
      openBNBLModal()

      offerPageDispatch({
        type: OfferPageActions.toggleFlightsPrice,
        isFlightsSelected: false,
      })

      const selectedPackages = Object.values(packagesByRoom)
      const groupedPackagesRecords = groupBy(selectedPackages, pkg => pkg.package.uniqueKey)
      const packages = [...groupedPackagesRecords.entries()].map(groupedPackage => {
        const basePackage = baseBnblPackageViews.find(packageView => packageView.package.uniqueKey === groupedPackage[0])
        const numberOfRooms = groupedPackage[1].length

        return {
          packageView: getBnblPackageViewByNumberOfRooms(basePackage, numberOfRooms),
          numberOfRooms,
        }
      })

      dispatch(initialiseBnblOfferModal(packages, offer.id, false))
    }

    if (!isLoggedIn) {
      e.preventDefault()

      const result = await showModal<AccountAccessModalResult>(<AccountAccessModal />)
      if (result.loggedIn) {
        openAndInitaliseBnblModal()
      }
    }

    if (isLoggedIn) {
      openAndInitaliseBnblModal()
    }
  }, [isLoggedIn, openBNBLModal, offerPageDispatch, packagesByRoom, dispatch, offer.id, baseBnblPackageViews, showModal])

  const onRemoveRoom = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const index = Number(e.currentTarget.dataset.idx)
    const newRooms = rooms?.filter((room, idx) => idx !== index)

    setChangedRoom(null)

    offerPageDispatch({
      type: OfferPageActions.setRooms,
      rooms: newRooms,
    })
  }, [rooms, offerPageDispatch])

  const feedbackMessage = useMemo(() => {
    return ({
      error: (title: string, message: string = '') => showSnackbar(message, 'critical', { heading: title }),
      success: (title: string, message: string = '') => showSnackbar(message, 'success', { heading: title }),
    })
  }, [])

  const getChangedRoomsFeedbackMessage = useCallback(() => {
    const resultMessage: FeedbackBannerMessage = {
      error: null,
    }

    const changedRoomsIds =
      Object
        .keys(packagesByRoom)
        .filter(roomId => roomId in previousPackagesByRoom.current && packagesByRoom[roomId]?.package.uniqueKey !== previousPackagesByRoom.current[roomId]?.package.uniqueKey)

    if (changedRoomsIds.length) {
      const roomsIdsWithoutPackage = changedRoomsIds.filter(roomId => !packagesByRoom[roomId])
      const roomsIdsWithPackage = changedRoomsIds.filter(roomId => packagesByRoom[roomId])

      if (roomsIdsWithoutPackage.length) {
        const roomsToString = roomsIdsWithoutPackage.map(roomId => `Room ${rooms.findIndex(room => room.roomId === roomId) + 1}`)
        resultMessage.error = {
          title: `No available packages for ${arrayToStringListFormat(roomsToString, 'and')}`,
          message: 'Please revise number of guests or adjust selected dates.',
        }
      } else {
        const didDurationChanged = previousDuration.current != duration
        const roomsWithDifferentRoomType = roomsIdsWithPackage.filter(roomId => roomTypesByRoom[roomId] && roomTypesByRoom[roomId] != packagesByRoom[roomId].package.roomType.id)

        if (didDurationChanged && roomsWithDifferentRoomType.length) {
          const roomsToString = roomsWithDifferentRoomType.map(roomId => `Room ${rooms.findIndex(room => room.roomId === roomId) + 1}`)
          resultMessage.error = {
            title: `The room type for ${arrayToStringListFormat(roomsToString, 'and')} is unavailable for selected duration`,
            message: 'We have selected the lowest price package available',
          }
        }
      }
    }

    return resultMessage
  }, [packagesByRoom, previousPackagesByRoom, rooms, previousDuration, duration, roomTypesByRoom])

  const getNewRoomsFeedbackMessage = useCallback(() => {
    const resultMessage: FeedbackBannerMessage = {
      error: null,
    }

    const previousRoomsIds = new Set(Object.keys(previousPackagesByRoom.current))
    const currentRoomsIds = Object.keys(packagesByRoom)
    if (previousRoomsIds.size < currentRoomsIds.length) {
      const newRoomsIds = currentRoomsIds.filter(roomId => !previousRoomsIds.has(roomId))
      const roomsIdsWithoutPackage = newRoomsIds.filter(roomId => !packagesByRoom[roomId])

      if (roomsIdsWithoutPackage.length) {
        const roomsIndexes = roomsIdsWithoutPackage.map(roomId => `Room ${rooms.findIndex(room => room.roomId === roomId) + 1}`)
        resultMessage.error = {
          title: `No available packages for ${arrayToStringListFormat(roomsIndexes, 'and')}`,
          message: 'Please revise number of guests or adjust selected dates.',
        }
      }
    }

    return resultMessage
  }, [packagesByRoom, previousPackagesByRoom, rooms])

  useEffect(() => {
    const isCalendarsLoading = Object.values(isLoadingByRoom).some(Boolean) || Object.values(calendarsToBeFetchedByRoom).some(Boolean)
    if (isCalendarsLoading || activeFilter) return

    const changedRoomsFeedbackMessage = getChangedRoomsFeedbackMessage()
    const newRoomsFeedbackMessage = getNewRoomsFeedbackMessage()

    if (changedRoomsFeedbackMessage.error) {
      feedbackMessage.error(
        changedRoomsFeedbackMessage.error.title,
        changedRoomsFeedbackMessage.error.message,
      )
    } else if (newRoomsFeedbackMessage.error) {
      feedbackMessage.error(
        newRoomsFeedbackMessage.error.title,
        newRoomsFeedbackMessage.error.message,
      )
    }

    Object.keys(packagesByRoom).forEach(roomId => {
      if (!(roomId in lastValidPackagesIdsByRoom.current) || packagesByRoom[roomId]) {
        lastValidPackagesIdsByRoom.current[roomId] = packagesByRoom[roomId]?.package.uniqueKey
      }
    })

    previousPackagesByRoom.current = packagesByRoom
    previousDuration.current = duration
    selectionAmountByPackage.current = countBy(packagesByRoom, (pkg) => pkg?.package.uniqueKey)
  }, [
    isLoadingByRoom, calendarsToBeFetchedByRoom, packagesByRoom, previousPackagesByRoom, feedbackMessage,
    activeFilter, duration, getNewRoomsFeedbackMessage, getChangedRoomsFeedbackMessage,
  ])

  const openOccupancyPicker = useCallback(() => {
    offerPageDispatch({
      type: OfferPageActions.toggleFilter,
      filter: 'capacity',
    })
  }, [offerPageDispatch])

  const onToggleConnectedRooms = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.toggleConnectedRooms })
  }, [offerPageDispatch])

  const shouldPreventMultipleRoomsBooking = rooms.length > 2 && canRedeemLuxPlusBenefits && totalRoomsBaseMemberPrice > 0 && !isCustomerSupportMode

  const callbacksFeatureEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.hotelsAndVillasRequestAgentCallbackEnabled)
  const callbackButtonProps = callbacksFeatureEnabled ? {
    children: 'Request a callback',
    onClick: () => showModal(<RequestAgentCallbackModal type="HOTELS_AND_VILLAS"/>),
  } : undefined

  const packageStartRef = useRef<HTMLDivElement>(null)
  const reachedAnchor = useHasGotToAnchor(packageStartRef?.current, -500, 25)

  useEffect(() => {
    onStickyFooterToggle(!reachedAnchor)
  }, [onStickyFooterToggle, reachedAnchor])

  const luxLoyaltyHotelOptions = useMemo<Array<App.LuxLoyaltyPointsCalculatorOptions | undefined>>(() => {
    return Object.values(packagesByRoom)?.map(packageView => (
      generateLuxLoyaltyPointsCalculatorHotelOptions(offer, { packageView })
    ))
  }, [offer, packagesByRoom])

  const { totalPointsCalculation } = useLuxLoyaltyPointsCalculator([
    ...luxLoyaltyHotelOptions,
    generateLuxLoyaltyPointsCalculatorFlightOptions(offer, {
      flightPrice: totalRoomsFlightPrice,
      skip: !isFlightsForAllRoomsSelected,
    }),
  ])

  return (
    <Pane ref={packageStartRef}>
      <PackageOptionsContainer ref={reviewPageRef}>
        <VerticalSpacer gap={24}>
          <StyledHeaderContainer>
            <Group direction="horizontal" tabletDirection="vertical" gap={8} tabletGap={0} verticalAlign="center" tabletVerticalAlign="start">
              <Heading variant="heading3">Your {rooms.length} rooms</Heading>
              <BodyText variant="medium" colour="neutral-two">{occupantsString}</BodyText>
            </Group>

            {isHotelAndFlightsToggleEnabled && (<>
              <CSSBreakpoint max="mobile">
                <HotelFlightsToggleButtonV2 />
              </CSSBreakpoint>
              <CSSBreakpoint min="tablet">
                <Group direction="horizontal" gap={8} verticalAlign="center" horizontalAlign="end">
                  <BodyText variant="medium">Adjust all rooms:</BodyText>
                  <FilterChip
                    size="medium"
                    disabled={buyNowBookLater || !isFlightsForAllRoomsAvailable}
                    selected={isFlightsForAllRoomsSelected}
                    onClick={onToggleFlightsForAllRooms}
                  >
                    {isFlightsForAllRoomsSelected ? 'Flights added' : 'Add flights'}
                  </FilterChip>
                </Group>
              </CSSBreakpoint>
            </>)}
          </StyledHeaderContainer>
          {rooms.map((room, index) => (
            <StyledPackageContainer key={index}>
              <StyledRoomPackageHeader>
                <div>
                  <Group direction="horizontal" verticalAlign="center">
                    <StyledRoomTitle variant="large" weight="bold">Room {index + 1} of {rooms.length}</StyledRoomTitle>
                    <StyledRoomOccupancyString variant="medium" colour="neutral-two">({formatOccupantsShort(room)})</StyledRoomOccupancyString>
                  </Group>
                  <StyledRemoveButton kind="tertiary" data-idx={index} onClick={onRemoveRoom}>
                    Remove
                  </StyledRemoveButton>
                </div>
                {isCheapestPackagesActiveByRoom[room.roomId] &&
                  <StatusLabel variant="confirmation">Best price selected</StatusLabel>
              }
              </StyledRoomPackageHeader>
              {!packagesByRoom[room.roomId] && (
                <NoPackagesContainer>
                  <BodyText variant="large" weight="bold">No packages available</BodyText>
                  <NoPackagesTextBox>
                    {lastValidPackagesIdsByRoom.current[room.roomId] && (
                      <BodyText variant="medium">
                        <TextLink weight="regular" onClick={openOccupancyPicker}>Add another room</TextLink>{' '}
                        to accommodate your number of guests, or{' '}
                        <TextLink weight="regular" onClick={openOccupancyPicker}>Revise number of guests</TextLink>
                      </BodyText>
                    )}
                    {!lastValidPackagesIdsByRoom.current[room.roomId] && (
                      <TextLink weight="regular" onClick={openOccupancyPicker}>Please revise number of guests</TextLink>
                    )}
                  </NoPackagesTextBox>
                </NoPackagesContainer>
              )}
              {packagesByRoom[room.roomId] && (
                <MultipleRoomsBookingOption
                  key={index}
                  offer={offer}
                  room={room}
                  isFlightsEnabled={!buyNowBookLater && flightsInfoByRoom[room.roomId]?.isFlightsAvailable}
                  packageOption={packagesByRoom[room.roomId]}
                  packageOptionSelectionsAmount={selectionAmountByPackage.current.get(packagesByRoom[room.roomId].package.uniqueKey)}
                  isFetching={isLoadingByRoom[room.roomId] || calendarsToBeFetchedByRoom[room.roomId]}
                  isFlightsSelected={flightsInfoByRoom[room.roomId]?.isFlightsSelected}
                  buyNowBookLater={buyNowBookLater}
                  openRoomsModal={onOpenRoomsModal}
                  isUpgradable={config.PACKAGE_CHANGE_UPGRADE_ENABLED && availablePackagesByRoom[room.roomId]?.length > 1}
                  upgradeButtonText={isCheapestPackagesActiveByRoom[room.roomId] ? 'Upgrade room' : 'Change room'}
                  availablePackagesQuantity={availablePackagesByRoom[room.roomId]?.length}
                />
              )}
            </StyledPackageContainer>
          ))}

          <FooterContainer>
            <RequestConnectingRoomsInput
              checked={isMultiRoomConnected}
              onToggleConnectRooms={onToggleConnectedRooms}
            />
          </FooterContainer>

          <PointsContainer>
            <LuxLoyaltyPoints
              pointsEarnCalculation={totalPointsCalculation}
              calculationType={(!checkIn || isFlightsForAllRoomsSelected) ? 'estimate' : 'exact'}
            />
          </PointsContainer>

          <MultipleRoomsBookingContentFooter
            ref={footerRef}
            sticky={!isMobileDevice && !footerInView && reviewPageInView}
          >
            <Wrapper className={clsx({ 'with-banner': shouldPreventMultipleRoomsBooking })}>
              {offer.hasBuyNowBookLater && !shouldPreventMultipleRoomsBooking && (
                <CSSBreakpoint min="desktop">
                  <TextButton kind="tertiary" onClick={onOpenBnblModal} horizontalOutdent="start">
                    Buy now choose dates later
                  </TextButton>
                  <Caption variant="medium" as="span" wrap="no-wrap">
                    Travel until {moment(offer.travelToDate).format(DMY_CASUAL_FORMAT)}
                  </Caption>
                </CSSBreakpoint>
              )}

              <StyledActionFooterButtons>
                <LuxPlusPriceGuard
                  showMemberPrice={totalRoomsBaseMemberPrice > 0}
                  fallbackPrice={
                    <div>
                      <PriceRowPriceCaption>
                        {rooms.length} rooms {hasNoFlightPrices ? 'from' : ''}
                      </PriceRowPriceCaption>
                      <PriceRowPrice
                        size="S"
                        price={totalRoomsPrice}
                        saleUnit={isPerNightPricingTestEnabled ? 'night' : 'total'}
                      />
                    </div>}
                >
                  <PriceStackLuxPlusCompactPrice
                  size="M"
                  memberPrice={totalRoomsMemberPrice}
                  saleUnit={isPerNightPricingTestEnabled ? 'night' : 'total'}
                  nonMemberPrice={totalRoomsPrice}
                  horizontalAlign="start"
                />
                </LuxPlusPriceGuard>
                {!shouldPreventMultipleRoomsBooking && <Group direction="horizontal" gap={8}>
                  {userMultiItemCartEnabled && !isFlightsForAllRoomsSelected && <MultiRoomOfferAddToCartButton disabled={isBookNowBtnDisabled} />}
                  {userMultiItemCartEnabled && isFlightsForAllRoomsSelected && <MultiRoomOfferSelectRoomsButton disabled={isBookNowBtnDisabled} />}
                  {(!userMultiItemCartEnabled || (userMultiItemCartEnabled && !isFlightsForAllRoomsSelected)) && <MultiRoomOfferBookButton disabled={isBookNowBtnDisabled} />}
                </Group>}
              </StyledActionFooterButtons>

              {offer.hasBuyNowBookLater && footerInView && !shouldPreventMultipleRoomsBooking && (
                <CSSBreakpoint max="tablet">
                  <Group direction="horizontal" gap={8} verticalAlign="center">
                    <TextButton kind="tertiary" onClick={onOpenBnblModal} horizontalOutdent="start">
                      Buy now choose dates later
                    </TextButton>
                    <Caption variant="medium" as="span" wrap="no-wrap">
                      Travel until {moment(offer.travelToDate).format(DMY_CASUAL_FORMAT)}
                    </Caption>
                  </Group>
                </CSSBreakpoint>
              )}
              {shouldPreventMultipleRoomsBooking && <MessageBanner
              kind="informational-primary"
              heading="Ready to book? Call us to complete your booking" description={`Our team are available ${NO_24_7_SUPPORT_REGIONS.includes(currentRegionCode) ? '7 days a week from 9am to 7pm' : '24 hours, 7 days'}`}
              primaryActionButtonProps={
                {
                  startIcon: <LinePhoneIcon />,
                  children: `Call ${currentContact.defaultContact.humanReadable}`,
                  href: `tel:${currentContact.defaultContact.number}`,
                }
              }
              secondaryActionButtonProps={callbackButtonProps}
            />}
            </Wrapper>
          </MultipleRoomsBookingContentFooter>
          {buyNowBookLater && <BnblModal closeBnblModal={closeBNBLModal} isOpen={buyNowBookLater} offer={offer} />}
          <MultipleRoomsBookingRoomsModal
            isOpen={isOpenRoomsModal}
            room={changedRoom}
            title={`Upgrade package for room ${changedRoomIndex + 1} of ${rooms.length}`}
            subtitle={`${availablePackagesByRoom[changedRoom?.roomId]?.length || 0} packages available`}
            availablePackages={availablePackagesByRoom[changedRoom?.roomId] || []}
            selectedPackageOption={packagesByRoom[changedRoom?.roomId]}
            closeModal={closeRoomsModal}
            onSelect={onSelectPackage}
            isFlightsSelected={flightsInfoByRoom[changedRoom?.roomId]?.isFlightsSelected}
            isFlightsEnabled={!buyNowBookLater && flightsInfoByRoom[changedRoom?.roomId]?.isFlightsAvailable}
          />
        </VerticalSpacer>
      </PackageOptionsContainer>
    </Pane>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  duration: state.duration,
  rooms: state.rooms,
  offer: state.offer,
  isMultiRoomConnected: state.isMultiRoomConnected,
  activeFilter: state.activeFilter,
  account: appState.auth.account,
  cheapestPackagesByRoom: getCheapestPackagesByRoom(state, appState),
  activePackagesByRoom: getActivePackagesByRoom(state, appState),
  isLoadingByRoom: isLoadingByRoom(state, appState),
  calendarsToBeFetchedByRoom: getCalendarsToBeFetchedByRoom(state, appState),
  flightsInfoByRoom: getFlightsInfoByRoom(state, appState),
  availablePackagesByRoom: getAvailablePackagesByRoom(state, appState),
  durationPackagesByRoom: getDurationPackageOptionsByRoom(state, appState),
  loggedIn: selectLoggedIn(appState),
  baseBnblPackageViews: getBnblPackageViews(state),
  roomTypesByRoom: mergeObjects(state.multipleRoomPackages.map(mrpkg => ({ [mrpkg.roomId]: mrpkg.roomTypeId }))),
  isPerNightPricingTestEnabled: !!getOptimizelyExperimentVariation(appState, OptimizelyExperiments.pricePerNight),
  canRedeemLuxPlusBenefits: checkCanRedeemLuxPlusBenefits(appState),
  isCustomerSupportMode: isCustomerSupportMode(appState),
  userMultiItemCartEnabled: !!getOptimizelyExperimentVariation(appState, OptimizelyExperiments.croMultiItemCart),
  checkIn: state.checkInDate,
}))(MultipleRoomsBookingContent)
